import { Component, inject, Input, OnChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { NgIf } from '@angular/common';
import { NoWidgetDataComponent } from '../../../../components/my-brand-screen/no-widget-data/no-widget-data.component';
import { WidgetValueField } from '../../../types/common';
import { Format, UtilService } from '../../../../services/util.service';
import { MyCompetitionPerformanceWidgetResponse } from '../../../../components/my-competition-screen/types';
import { LoaderComponent } from '../../loader/loader.component';
import { fakeAsync } from '@angular/core/testing';
import { AssetVersionPipe } from '../../../../pipes/asset-version.pipe';

@Component({
  selector: 'app-performance-over-time',
  imports: [
    HighchartsChartModule,
    NgIf,
    NoWidgetDataComponent,
    LoaderComponent,
    AssetVersionPipe
  ],
  templateUrl: './performance-over-time.component.html',
  standalone: true,
  styleUrl: './performance-over-time.component.scss'
})
export class PerformanceOverTimeComponent implements OnChanges {
  @Input() brandColors: Record<string, string> = {};
  @Input() format: Format = 'fractionPercentage';
  @Input() valueField: WidgetValueField = '';
  @Input() data?: MyCompetitionPerformanceWidgetResponse;
  @Input() loading = false;
  @Input() isUnderDateThreshold = false;

  utilService = inject(UtilService);
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  chartInstance: Highcharts.Chart | undefined;

  chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
    this.chartInstance = chart;
  };

  ngOnChanges(): void {
    if (!this.data?.myBrand) return;

    const categories = this.data?.myBrand?.dailyPerformance.map(dp => dp.date);
    const competitorSeries: Highcharts.SeriesSplineOptions[] = (this.data?.myCompetitors || [])
      .map((competitor, index) => ({
        name: competitor.brandName,
        data: competitor.dailyPerformance.map(dp => dp.value ?? 0),
        type: 'spline',
        marker: { symbol: 'circle' },
        dashStyle: 'Solid',
        color: this.brandColors[competitor.brandKey] || undefined
      }));
    const allSeries: Highcharts.SeriesSplineOptions[] = [
      {
        name: this.data?.myBrand?.brandName,
        data: this.data?.myBrand?.dailyPerformance.map(dp => dp.value ?? 0),
        type: 'spline',
        marker: { symbol: 'circle' },
        dashStyle: 'ShortDash',
        color: this.brandColors[this.data.myBrand.brandKey]
      },
      ...competitorSeries
    ];

    while (this.chartInstance?.series?.length || 0 > 0) {
      this.chartInstance?.series[0].remove(false);
    }
    allSeries.forEach(s => this.chartInstance?.addSeries(s, false));

    const step = this.calculateStep(categories);

    this.chartOptions = {
      chart: {
        type: 'spline',
        spacingBottom: -5,
      },
      title: {
        text: undefined
      },
      xAxis: {
        categories: categories as string[],
        lineColor: 'transparent',
        labels: {
          style: {
            fontFamily: 'Poppins',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '18px',
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: '#686D75',
            lineClamp: 1,
          },
          autoRotation: [],
          autoRotationLimit: 0,
          step: 1,
          rotation: -90,
          formatter: function () {
            const index = this.pos;
            return index % step === 0 ? String(this.value) : '';
          }
        }
      },
      yAxis: {
        title: { text: "" },
        labels: {
          style: {
            color: '#686D75',
            fontSize: '12px',
            fontFamily: 'Poppins',
            fontWeight: '400',
            lineHeight: '18px',
            fontStyle: 'normal',
          },
          formatter: (function (utilService, format) {
            return function () {
              // Handle special case for shortCurrencyNumber format when value is less than 1000
              if (format === 'shortCurrencyNumber' && (this.value as number) < 1_000) {
                return '0';
              }
              return utilService.formatWidgetValue(this.value as number, format) as string;
            };
          })(this.utilService, this.format),
        }
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true,
                width: 12,
                height: 12,
              }
            },
          },
          showInLegend: true
        }
      },
      legend: {
        useHTML: true,
        symbolWidth: 0,
        align: 'left',
        alignColumns: true,
        itemDistance: 15,
        events: {
          itemClick: () => false,
        },
        itemHoverStyle: {
          cursor: 'default'
        },
        itemStyle: {
          color: '#686D75',
          fontSize: '12px',
          fontFamily: 'Poppins',
          fontWeight: '400',
          lineHeight: '18px',
          fontStyle: 'normal',
        },
        labelFormatter: function () {
          const color = this.color;
          return `
            <span style="display: inline-block;
                        width: 10px; height: 10px;
                        background-color: ${color};
                        border-radius: 50%;
                        margin-right: 3px;">
            </span> ${this.name}
          `;
        }
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        useHTML: true,
        shadow: false,
        backgroundColor: 'transparent',
        formatter: (function (utilService, format) {
          return function () {
            const chart = this.points?.[0]?.series?.chart;
            const allSeries = chart?.series || [];
            let content = `<div style="
              display: flex;
              flex-direction: column;
              padding: 12px;
              gap: 6px;
              color: #34363A;
              font-weight: 400;
              font-style: normal;
              font-family: Poppins, sans-serif;
              font-size: 12px;
              background: #FFFFFF;
              border-radius: 4px;
              box-shadow: 0px 0px 8px 2px rgba(0, 0, 0, 0.15);
              ">
              <b>${this.category}</b>
            `;
            allSeries.forEach((series, index) => {
              if (!series.visible) return;
              const point = series.data.find(p => p.x === this.x);
              if (index === 1) {
                content += `<hr style="border: none; border-top: 1px dashed #CDD1D7; margin: 1px 0;" />`;
              }
              const value = point?.y ? utilService.formatWidgetValue(point?.y as number, format) : utilService.formatWidgetValue(0, format);
              const color = series.color;
              content += `<div><span style="color:${color}; font-size:16px;">\u25CF</span> ${series.name} <b>${value}</b></div>`;
            });
            content += `</div>`;
            return content;
          }
        })(this.utilService, this.format),
      },
      series: allSeries,
    }
  }

  private calculateStep(categories: (string | undefined)[]): number {
    const total = categories?.length || 0;
    if (total <= 6) return 1;

    const maxLabels = 8;
    const step = Math.ceil(total / maxLabels);

    return step;
  }

  public get isPerformanceData(): boolean {
    return !!this.data && (this.data.myBrand?.dailyPerformance?.length || 0) > 0;
  }

  protected readonly fakeAsync = fakeAsync;
}
